import { useEffect, useState, useMemo } from 'react';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { browserLogger } from 'utils/logger/browserLogger';

export function useLastSearches() {
  const [lastSearches, setLastSearches] = useState<string[]>([]);
  const MAX_SEARCHES_LIMIT = 5;
  const STORAGE_KEY = 'lastSearchesStorage';

  const recentSearchesPlugin = useMemo(
    () =>
      createLocalStorageRecentSearchesPlugin({
        key: STORAGE_KEY,
        limit: MAX_SEARCHES_LIMIT
      }),
    []
  );

  async function cleanupLocalStorage() {
    if (!recentSearchesPlugin.data) return;

    try {
      const rawStorage = localStorage.getItem(STORAGE_KEY);
      if (!rawStorage) return;

      const data = JSON.parse(rawStorage);
      const rawItems = data?.items || [];

      if (rawItems.length > MAX_SEARCHES_LIMIT) {
        const oldestItem = rawItems[rawItems.length - 1];
        recentSearchesPlugin.data.removeItem(oldestItem.id);
      }
    } catch (error) {
      browserLogger().error({
        message: 'Erro ao limpar localStorage de Buscas Recentes',
        data: String(error)
      });
    }
  }

  async function refreshLastSearches() {
    try {
      if (recentSearchesPlugin.data) {
        const searches = recentSearchesPlugin.data.getAll();
        const items = await Promise.resolve(searches);

        if (Array.isArray(items) && items.length > 0) {
          const mapped = items
            .map((item) => item.label || item.id)
            .slice(0, MAX_SEARCHES_LIMIT);

          setLastSearches(mapped);
        } else {
          setLastSearches([]);
        }
      }
    } catch (error) {
      browserLogger().error({
        message: 'Erro ao atualizar pesquisas recentes no localStorage',
        data: String(error)
      });
    }
  }

  function updateLastSearches(text: string) {
    if (text && recentSearchesPlugin.data) {
      try {
        recentSearchesPlugin.data.addItem({
          id: text,
          label: text
        });

        Promise.resolve().then(() => {
          cleanupLocalStorage().then(refreshLastSearches);
        });
      } catch (error) {
        browserLogger().error({
          message: 'Erro ao adicionar pesquisa recente no localStorage',
          data: String(error)
        });
      }
    }
  }

  async function removeLastSearch(text: string) {
    try {
      if (recentSearchesPlugin.data) {
        recentSearchesPlugin.data.removeItem(text);
        await cleanupLocalStorage();
        await refreshLastSearches();
      }
    } catch (error) {
      browserLogger().error({
        message: 'Erro ao remover pesquisa recente do localStorage',
        data: String(error)
      });
    }
  }

  useEffect(() => {
    refreshLastSearches();
  }, []);

  return {
    lastSearches,
    updateLastSearches,
    removeLastSearch
  };
}
